import { useRef, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import {
  Breadcrumbs,
  Button,
  Badge,
  SkeletonBase,
  formatAmountWithCommas,
  FormatCurrencyForDisplay,
  Input,
  Modal,
} from "@unified-checkout/design-system";
import { Copy, Xmark, PaperPlane, Print, Download } from "@unified-checkout/design-system/icons/index";
import { Danger, SuccessHeader } from "@unified-checkout/design-system/icons/state_icons";

import type { ITagProps } from "libs/design-system/src/types/tag/tag";
import { StatusContent } from "@app/dashboard/components/inner_layout/StatusContent";
import { CANCEL_QUICK_LINK_MODAL_STEP_STATUS } from "@app/dashboard/constants/quick-links";
import { FOUR_SECONDS } from "@app/dashboard/constants/shared";
import { useLangStore } from "@app/dashboard/store/lang";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { formatToLocalizedDateTime } from "@app/dashboard/utils/dateFormats";
import { convertCentsToMajorUnit } from "@app/dashboard/utils/convertAmounts";
import { notify } from "@app/dashboard/utils/notifier";
import { cancelQuickLink, shareQuickLink } from "@app/dashboard/services/inner_layout/quick-links";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { LINK_STATES } from "@app/dashboard/constants/quick-links";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { PERMISSIONS } from "@app/dashboard/constants/permissions";
import { PROFILE } from "@app/dashboard/constants/profile";
import { isAccessible } from "@app/dashboard/utils/isAllowedPermission";
import { userInfo } from "@app/dashboard/utils/auth/userInfo";
import ShareModal from "@app/dashboard/components/shared/modals/ShareModal";
import { useQRCode } from "@app/dashboard/hooks/useQRCode";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";

import styles from "./header.module.scss";

interface ISingleQuickLinkHeader {
  id: number;
  data: any;
  isError: boolean;
  isLoading: boolean;
  refetchData: () => void;
}


export const Header = ({
  id,
  data: quickLink,
  isError,
  isLoading,
  refetchData
}: ISingleQuickLinkHeader) => {
  const [restTags, setRestTags] = useState(0);
    
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modalBodyRef = useRef<HTMLDivElement | null>(null);
  
  const { lang } = useLangStore();
  const { profileType, companyName } = userInfo();
  const screenWidth = useScreenWidth();
  const {
    printModal,
    isLoading: isQRCodeLoading,
    downloadModalAsPNG
  } = useQRCode(modalBodyRef);
  
  const hasSharePermission = isAccessible(PERMISSIONS.CAN_SHARE_QUICK_LINKS);
  const hasCancelPermission = isAccessible(PERMISSIONS.CAN_CANCEL_QUICK_LINKS);
  const canShareQuickLinks = profileType === PROFILE.TYPE.MERCHANT || (profileType === PROFILE.TYPE.MERCHANT_STAFF && hasSharePermission);
  const canCancelQuickLinks = profileType === PROFILE.TYPE.MERCHANT || (profileType === PROFILE.TYPE.MERCHANT_STAFF && hasCancelPermission);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cancelModalStep, setCancelModalStep] = useState<{
    type: (typeof CANCEL_QUICK_LINK_MODAL_STEP_STATUS)[keyof typeof CANCEL_QUICK_LINK_MODAL_STEP_STATUS];
    message: string;
  }>({ type: CANCEL_QUICK_LINK_MODAL_STEP_STATUS.START, message: '' });

  const amount = quickLink?.amount_cents && quickLink?.currency
    ? convertCentsToMajorUnit(quickLink.amount_cents, quickLink.currency)
    : null;
  const currency = quickLink?.currency;
  const status = quickLink?.state;
  const created_at = quickLink?.created_at;
  const paymentLinkUrl = quickLink?.shorten_url || quickLink?.client_url || '';
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const getBadgeProps = (state: string) => {
    switch (state) {
      case LINK_STATES.PAID:
        return { color: 'green' as const, text: t('innerLayout.quickLinksPage.filters.states.paid') };
      case LINK_STATES.UNPAID:
      case 'created':
        return { color: 'yellow' as const, text: t('innerLayout.quickLinksPage.filters.states.unpaid') };
      case LINK_STATES.CANCELLED:
        return { color: 'red' as const, text: t('innerLayout.quickLinksPage.filters.states.cancelled') };
      case LINK_STATES.DEACTIVATED:
        return { color: 'yellow' as const, text: t('innerLayout.quickLinksPage.filters.states.deactivated') };
      default:
        return { color: 'yellow' as const, text: t('innerLayout.quickLinksPage.filters.states.unpaid') };
    }
  };

  const handleCopyLink = async () => {
    if (paymentLinkUrl) {
      try {
        await navigator.clipboard.writeText(paymentLinkUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
    }
  };

  const handleCancelLink = () => {
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false);
    setCancelModalStep({ type: CANCEL_QUICK_LINK_MODAL_STEP_STATUS.START, message: '' });
  };

  const handleCancelModalStatus = (
    type: (typeof CANCEL_QUICK_LINK_MODAL_STEP_STATUS)[keyof typeof CANCEL_QUICK_LINK_MODAL_STEP_STATUS],
    message: string
  ) => {
    setCancelModalStep({ type, message });
    setTimeout(() => {
      if (type === CANCEL_QUICK_LINK_MODAL_STEP_STATUS.SUCCESS) refetchData();
      handleCloseCancelModal();
    }, FOUR_SECONDS);
  };

  const { isPending: isCancelLoading, mutate: mutateCancelLink } = useMutation({
    mutationKey: [QUERY_KEYS.CANCEL_QUICK_LINK],
    mutationFn: () => cancelQuickLink(id),
    onSuccess: (response) => {
      const success = response?.data?.success;
      const message = response?.data?.message;

      if (success) {
        handleCancelModalStatus(
          CANCEL_QUICK_LINK_MODAL_STEP_STATUS.SUCCESS,
          message || t('innerLayout.quickLinksPage.singleQuickLink.cancelSuccess')
        );
      } else {
        handleCancelModalStatus(
          CANCEL_QUICK_LINK_MODAL_STEP_STATUS.FAILED,
          message || t('innerLayout.errors.smthWentWrong')
        );
      }
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const message = response?.data?.message;

      handleCancelModalStatus(
        CANCEL_QUICK_LINK_MODAL_STEP_STATUS.FAILED,
        message || t('innerLayout.errors.smthWentWrong')
      );
    },
  });

  const handleConfirmCancel = () => {
    mutateCancelLink();
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  const { mutateAsync: mutateSharePaymentLink, isPending: isPendingShare } =
    useMutation({
      mutationFn: ({
        paymentLinkId,
        contacts,
      }: {
        paymentLinkId: number;
        contacts: string[];
      }) => shareQuickLink(paymentLinkId, contacts),
      onSuccess: () => {
        notify.success(t('innerLayout.quickLinksPage.singleQuickLink.shareModal.sharedSuccess'));
        setRestTags((prev) => prev + 1);
      },
      onError: (axiosErr: AxiosError<{ message?: string }>) => {
        let message = t('errors.somethingWentWrong');

        if (axiosErr.response?.data?.message) {
          message = axiosErr.response.data.message;
        } else if (axiosErr.message) {
          message = axiosErr.message;
        }

        notify.error(message);
      },
    });

  const handleShareQuickLink = async (contacts: ITagProps[]) => {
    if (!contacts.length) return;
    try {      
      return await mutateSharePaymentLink({ paymentLinkId: id, contacts: contacts.map((contact) => contact.text) });
    } catch (err) { }
  };

  const renderCancelModalContent = (): ReactElement | null => {
    if (
      cancelModalStep.type !== CANCEL_QUICK_LINK_MODAL_STEP_STATUS.SUCCESS &&
      cancelModalStep.type !== CANCEL_QUICK_LINK_MODAL_STEP_STATUS.FAILED
    ) {
      return null;
    }
    return (
      <StatusContent
        state={cancelModalStep.type === CANCEL_QUICK_LINK_MODAL_STEP_STATUS.FAILED ? 'danger' : 'success'}
        title={t(
          `innerLayout.quickLinksPage.singleQuickLink.cancelModal.${cancelModalStep.type}`
        )}
        supportingText={cancelModalStep.message}
        statusIcon={
          cancelModalStep.type === CANCEL_QUICK_LINK_MODAL_STEP_STATUS.SUCCESS ? SuccessHeader : Danger
        }
      />
    );
  };

  return (
    <div className={styles["header"]}>
      <div className={styles["header__breadcrumbs"]}>
        {
          isLoading ?
            <SkeletonBase height="1.5rem" />
            :
            <Breadcrumbs
              linksProps={[
                {
                  text: t('innerLayout.navbar.paymentLinks'),
                  onClickHandler: () => navigate(INNER_ROUTES.QUICK_LINKS),
                  active: true
                },
                {
                  text: t('innerLayout.quickLinksPage.singleQuickLink.linkId').replace('{{id}}', String(id)),
                }
              ]}
            />
        }
      </div>

      {
        isLoading ?
          <SkeletonBase height="3.75rem" />
          :
          !isError && quickLink && (
            <div className={styles["header__main-info-box"]}>
              <div className={styles["header__main-info-box__info"]}>
                <div className={styles["header__main-info-box__info__amount-status-box"]}>
                  <span>
                    {
                      amount && (
                        <>
                          {lang === 'en' && FormatCurrencyForDisplay(currency)}
                          {' '}{formatAmountWithCommas({ amount, currency })}{' '}
                          {lang === 'ar' && FormatCurrencyForDisplay(currency)}
                        </>
                      )
                    }
                  </span>

                  <Badge
                    size="md"
                    {...getBadgeProps(status)}
                  />
                </div>

                <div className={styles["header__main-info-box__info__date"]}>
                  {`${t('innerLayout.quickLinksPage.singleQuickLink.created')}
                  ${created_at && formatToLocalizedDateTime(created_at, lang)}`}
                </div>

                {status !== LINK_STATES.PAID && (
                  <div className={styles["header__main-info-box__info__payment-link"]}>
                    <div className={styles["payment-link-wrapper"]}>
                      <Input
                        type="text"
                        id="payment-link"
                        name="payment-link"
                        label={t('innerLayout.quickLinksPage.singleQuickLink.paymentLink')}
                        value={paymentLinkUrl}
                        disabled
                      />
                      {
                        status !== LINK_STATES.CANCELLED && (<>
                          {!copied && (
                            <button
                              type="button"
                              onClick={handleCopyLink}
                              className={styles["copy-button"]}
                              aria-label={t('innerLayout.quickLinksPage.singleQuickLink.copyLink')}
                              title={t('innerLayout.quickLinksPage.singleQuickLink.copyLink')}
                            >
                              {Copy}
                            </button>
                          )}
                          {copied && (
                            <span className={styles["copied"]}>
                              {t('innerLayout.quickLinksPage.singleQuickLink.linkCopied')}
                            </span>
                          )}
                        </>)
                      }
                    </div>
                  </div>
                )}
              </div>

              <div className={styles["header__main-info-box__actions"]}>
                {canCancelQuickLinks && status !== LINK_STATES.CANCELLED && status !== LINK_STATES.PAID && (
                  <Button
                    size="md"
                    text={t('innerLayout.quickLinksPage.singleQuickLink.cancelLink')}
                    variant="outlined"
                    color="danger"
                    leadingIcon={Xmark}
                    onClickHandler={handleCancelLink}
                  />
                )}
                {status !== LINK_STATES.PAID && (
                  <Button
                    size="md"
                    text={t('innerLayout.quickLinksPage.singleQuickLink.share')}
                    variant="contained"
                    color="primary"
                    leadingIcon={PaperPlane}
                    onClickHandler={handleShare}
                    disabled={status === LINK_STATES.CANCELLED}
                  />
                )}
              </div>
            </div>
          )
      }

      <ShareModal
        modalProps={{
          isModalOpen: isShareModalOpen,
          handleCloseModal: handleCloseShareModal,
          title: t("innerLayout.quickLinksPage.singleQuickLink.shareModal.title"),
          subtitle: t("innerLayout.quickLinksPage.singleQuickLink.shareModal.supportingText"),
          closeOnClickOutside: true,
          isOverlayAboveModal: true,
          hasCloseIcon: true,
        }}
        shareInputProps={{
          link: paymentLinkUrl,
          label: t('innerLayout.quickLinksPage.singleQuickLink.shareModal.shareLink'),
        }}
        inputTagsProps={{
          showTagsInput: canShareQuickLinks,
          resetTagsOnShare: restTags,
          tagsShareBtn: {
            onClickHandler: (tags: ITagProps[]) => handleShareQuickLink(tags),
            loading: isPendingShare,
            disabled: isPendingShare,
          },
        }}
        socialBtns={{
          "qr-code": {
            label: t(`innerLayout.quickLinksPage.singleQuickLink.shareModal.${isMobile ? 'qrCode' : 'scanPrintQR'}`),
          }
        }}
        QRCodeProps={{
          modalBodyRef: modalBodyRef,
          modalProps: {
            closeOnClickOutside: true,
            isModalOpen: true,
            handleCloseModal: () => { },
            footerBtn: [
              {
                text: t(
                  'innerLayout.quickLinksPage.singleQuickLink.shareModal.print'
                ),
                variant: 'contained',
                color: 'primary',
                size: 'md',
                leadingIcon: Print,
                onClickHandler: printModal,
              },
              {
                text: t(
                  'innerLayout.quickLinksPage.singleQuickLink.shareModal.download'
                ),
                variant: 'outlined',
                color: 'secondary',
                size: 'md',
                leadingIcon: Download,
                onClickHandler: downloadModalAsPNG,
                loading: isQRCodeLoading,
                disabled: isQRCodeLoading,
              },
            ],
          },
          cardProps: {
            showPaymobTheme: true,
            cardTitle: companyName,
          },
        }}
      />

      <Modal
        open={isCancelModalOpen}
        onCloseHandler={handleCloseCancelModal}
        closeOnClickOutside={false}
        hasCloseIcon={cancelModalStep.type !== CANCEL_QUICK_LINK_MODAL_STEP_STATUS.START}
        title={
          cancelModalStep.type === CANCEL_QUICK_LINK_MODAL_STEP_STATUS.START
            ? t('innerLayout.quickLinksPage.singleQuickLink.cancelModal.title')
            : ''
        }
        supportingText={
          cancelModalStep.type === CANCEL_QUICK_LINK_MODAL_STEP_STATUS.START
            ? t('innerLayout.quickLinksPage.singleQuickLink.cancelModal.supportingText')
            : undefined
        }
        state={cancelModalStep.type === CANCEL_QUICK_LINK_MODAL_STEP_STATUS.START ? 'danger' : undefined}
        headerIcon={cancelModalStep.type === CANCEL_QUICK_LINK_MODAL_STEP_STATUS.START ? Xmark : undefined}
        btnsProps={
          cancelModalStep.type === CANCEL_QUICK_LINK_MODAL_STEP_STATUS.START
            ? [
                {
                  text: t('innerLayout.quickLinksPage.singleQuickLink.cancelModal.yesCancel'),
                  variant: "contained" as const,
                  color: "danger" as const,
                  onClickHandler: handleConfirmCancel,
                  loading: isCancelLoading,
                },
                {
                  text: t('innerLayout.quickLinksPage.singleQuickLink.cancelModal.noGoBack'),
                  variant: "outlined" as const,
                  color: "secondary" as const,
                  onClickHandler: handleCloseCancelModal,
                  disabled: isCancelLoading,
                },
              ]
            : undefined
        }
      >
        {renderCancelModalContent()}
      </Modal>

      
    </div>
  );
};
